<template>
  <div class="manage-analytics-reports mt-3 mx-0 mx-sm-3">

    <b-nav pills class="mb-3">
      <b-nav-item :to="{ name: 'ManageAnalytics' }">Stats</b-nav-item>
      <b-nav-item active>Reports</b-nav-item>
      <template v-if="queries">
        <div class="flex-fill"/>
        <b-dropdown id="reports-query-selector" right text="Select Report" :disabled="isLoading">
          <b-dropdown-item v-for="query in queries" :key="query.key"
            @click="selectedQuery = query" :active="selectedQuery && query.id === selectedQuery.id">
              <p class="m-0">{{ query.name }}</p>
              <small v-if="query.description" class="d-block">{{ query.description }}</small>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </b-nav>

    <b-card class="mb-5">
      <b-card-title class="d-flex flex-row justify-content-start align-items-center" v-if="selectedQuery">
        {{ selectedQuery.name }}
        <div class="flex-fill mr-1"/>
        <span v-if="!isLoading && report" class="small mr-2">{{ $tc('manage.total-results', report.length) }}</span>
        <b-btn class="mr-1" :disabled="isLoading" @click="() => fetchData()" variant="outline-secondary">
          <font-awesome-icon icon="redo"/><span class="sr-only">Refresh</span>
        </b-btn>
        <b-dropdown :disabled="!report" id="download-btn" no-caret right variant="outline-secondary">
          <template v-slot:button-content><font-awesome-icon icon="download"/><span class="sr-only">Download</span></template>
          <b-dropdown-item @click="() => exportTableAs('csv')">CSV (.csv)</b-dropdown-item>
          <b-dropdown-item @click="() => exportTableAs('xlsx')">Excel (.xlsx)</b-dropdown-item>
        </b-dropdown>
      </b-card-title>

      <b-table striped hover responsive show-empty
        :fields="fields" :items="report" :per-page="20" :current-page="currentPage" :busy="isLoading">
          <template #head(jobID)>Job ID</template>
          <template #cell(jobID)="{ value: jobID }">
            <router-link :to="{ name: 'JobShow', params: { jobID } }">{{ jobID }}</router-link>
          </template>

          <template #head(url)>URL</template>
          <template #cell(url)="{ value }">
            <a :href="value" target="_blank">
              {{ (v => v.length > 32 ? `${v.slice(0, 30)}...` : v)(value.replace(/http(s)?:\/\/(www.)?/, '')) }}
            </a>
          </template>

          <template #empty>
            <b-alert class="m-5" variant="danger" show v-if="err">
              {{ err }}
            </b-alert>
            <div class="text-center my-2" v-else>
              Please select a report above
            </div>
          </template>
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>
      </b-table>

      <b-pagination v-if="Array.isArray(report) && report.length"
        v-model="currentPage" :total-rows="report.length" per-page="20" align="center"/>
    </b-card>

  </div>
</template>

<script>
import gql from 'graphql-tag';
import ExcelJS from 'exceljs';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    companyID: String,
  },
  metaInfo: {
    title: 'Custom Stats',
  },
  data: () => ({
    err: null,
    isLoading: false,
    selectedQuery: null,
    report: [],
    currentPage: 1,
  }),
  apollo: {
    queries: {
      query: gql`
        query getStatsReports {
          company {
            id
            statsReports
          }
        }
      `,
      update: ({ company: { statsReports } }) => JSON.parse(statsReports),
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isLoggedIn' ]),
    fields() {
      if (Array.isArray(this.report) && this.report.length) {
        return Object.keys(this.report[0]).map(key => {
          const field = {
            key,
            // sortable: true,
          };

          if (key === 'atsID') {
            field.label = 'ATS ID';
          }

          if (key === 'timestamp' || key.endsWith('At')) {
            field.formatter = value => value ? (new Date(value)).toLocaleString() : null;
          }

          return field;
        });
      } else {
        return null;
      }
    }
  },
  methods: {
    async fetchData() {
      try {
        this.err = null;
        this.isLoading = true;
        this.report = null;

        const { id } = this.selectedQuery;
        this.$log.debug('selectedQuery', { id });

        const { data, errors } = await this.$apollo.mutate({
          mutation: gql`
            mutation runStatsReport($id: ID!) {
              report: runStatsReport(id: $id)
            }
          `,
          variables: {
            id
          },
        });

        if (Array.isArray(errors) && errors.length) {
          errors.forEach(err => this.$log.error(err));
          throw errors[0];
        }

        this.$assert(data && data.report, new Error('Expected results of the query'));
        this.report = JSON.parse(data.report);
      } catch (err) {
        this.$log.error(err);
        this.err = 'An error occurred, please try again later.';
      } finally {
        this.currentPage = 1;
        this.isLoading = false;
      }
    },
    async exportTableAs(type) {
      try {
        this.$assert([ 'csv', 'xlsx' ].includes(type), new Error('Invalid file export type chosen'));

        const fields = Object.keys(this.report[0]);
        this.$assert(fields.length, new Error('No fields defined'));

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Table Export');

        worksheet.columns = fields.map(field => ({
          header: `${field}`,
          key: `${field}`,
        }));

        worksheet.addRows(this.report);
        // this.report.forEach(item => worksheet.addRow(item));

        const buffer = await workbook[type].writeBuffer();
        const blob = new Blob([ buffer ], { type: type === 'xlsx' ? 'application/vnd.ms-excel' : 'text/csv' });

        const link = document.createElement('a');
        link.download = `Real-Links-${this.company.name}-${this.selectedQuery.name}.${type}`;
        link.target = 'blank';
        link.href = URL.createObjectURL(blob);
        link.click();
      } catch (err) {
        this.$notifyErr(err, { prepend: 'Failed to export table:' });
      }
    },
  },
  watch: {
    async selectedQuery() {
      this.fetchData();
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/views/Recruiter/StatsReports.vue